import type { VolumeProfileRange } from './state/types';
import { ChronoUnit, DayOfWeek, Instant, LocalTime, ZoneId, ZonedDateTime } from '@js-joda/core';
import type { LocalDate } from '@js-joda/core';

const NEW_YORK = ZoneId.of('America/New_York');

/** Kiasi counts prints from 04:00 (inclusive) to 20:00 (exclusive) New York time. */
export const KIASI_SESSION_OPEN = LocalTime.of(4, 0);
export const KIASI_SESSION_CLOSE = LocalTime.of(20, 0);
export const REGULAR_SESSION_OPEN = LocalTime.of(9, 30);
export const REGULAR_SESSION_CLOSE = LocalTime.of(16, 0);

export interface VolumeProfileWindow {
    /** Inclusive lower bound on bar open time, epoch milliseconds. */
    readonly fromMs: number;
    /** Inclusive upper bound on bar open time, epoch milliseconds. */
    readonly toMs: number;
}

function atNewYork(nowMs: number): ZonedDateTime {
    return ZonedDateTime.ofInstant(Instant.ofEpochMilli(nowMs), NEW_YORK);
}

function epochMs(value: ZonedDateTime): number {
    return value.toInstant().toEpochMilli();
}

function isWeekend(date: LocalDate): boolean {
    const day = date.dayOfWeek();
    return day === DayOfWeek.SATURDAY || day === DayOfWeek.SUNDAY;
}

/** The most recent weekday on or before `date`. Exchange holidays are not known here (PLAT-12347 follow-up). */
function latestWeekday(date: LocalDate): LocalDate {
    let candidate = date;
    while (isWeekend(candidate)) candidate = candidate.minusDays(1);
    return candidate;
}

/**
 * Resolves a range selection into the bar-open window to request from Kiasi.
 *
 * Sliding ranges end at `nowMs` so the open bar is included once live data
 * merges in; their start is floored to the minute so consecutive polls see the
 * same closed bars. `day` covers Kiasi's whole session of the latest weekday;
 * before 04:00 New York, and all weekend, it falls back to the previous
 * weekday's session so the tab is not empty overnight. `custom` is the regular
 * session of the latest weekday until custom bounds are part of the persisted
 * state. Exchange holidays still resolve to an empty session.
 */
export function resolveVolumeProfileWindow(range: VolumeProfileRange, nowMs: number): VolumeProfileWindow {
    const now = atNewYork(nowMs);
    const minute = now.truncatedTo(ChronoUnit.MINUTES);
    switch (range) {
        case 'day': {
            const today = now.toLocalDate();
            const sessionOpen = today.atTime(KIASI_SESSION_OPEN).atZone(NEW_YORK);
            if (isWeekend(today) || now.isBefore(sessionOpen)) {
                const previousDay = latestWeekday(today.minusDays(1));
                return {
                    fromMs: epochMs(previousDay.atTime(KIASI_SESSION_OPEN).atZone(NEW_YORK)),
                    toMs: epochMs(previousDay.atTime(KIASI_SESSION_CLOSE).atZone(NEW_YORK)),
                };
            }
            return { fromMs: epochMs(sessionOpen), toMs: nowMs };
        }
        case '1h':
            return { fromMs: epochMs(minute.minusHours(1)), toMs: nowMs };
        case '5m':
            return { fromMs: epochMs(minute.minusMinutes(5)), toMs: nowMs };
        case '1m':
            return { fromMs: epochMs(minute.minusMinutes(1)), toMs: nowMs };
        case 'custom': {
            const today = latestWeekday(now.toLocalDate());
            return {
                fromMs: epochMs(today.atTime(REGULAR_SESSION_OPEN).atZone(NEW_YORK)),
                toMs: epochMs(today.atTime(REGULAR_SESSION_CLOSE).atZone(NEW_YORK)),
            };
        }
        default: {
            const _exhaustiveCheck: never = range;
            return { fromMs: nowMs, toMs: nowMs };
        }
    }
}
